"use client"
import { buttonVariants } from "@/components/ui/button"
import { useState } from "react"
import { FAQIcons } from "./icons"

export interface FAQItem {
  question: string
  answer: string
}

// Default FAQ data
export const defaultFaqs: FAQItem[] = [
  {
    question: "What are refurbished laptops?",
    answer:
      "Refurbished laptops are pre-owned devices that have been professionally restored to like-new condition. At Edify, each laptop undergoes rigorous testing, certified repairs, and deep cleaning to meet high-quality standards. Choosing a refurbished laptop is a smart way to get premium performance at a lower price—without compromising on quality or sustainability.",
  },
  {
    question: "Are refurbished laptops reliable?",
    answer:
      "Yes. Every Edify laptop goes through strict quality checks, hardware diagnostics, and functionality tests before being approved for sale. You get the reliability of a new device, backed by warranty—at a fraction of the cost.",
  },
  {
    question: "How do I know if a refurbished laptop is right for me?",
    answer:
      "If you’re looking for top-brand laptops at budget-friendly prices, a refurbished laptop is ideal. Whether you're a student, working professional, or small business owner, Edify offers reliable options tailored to your performance needs and budget.",
  },
  {
    question: "What is included with a refurbished laptop purchase?",
    answer: `
      Each Edify laptop includes:
      A charger
      
      A detailed condition report
      
      A minimum 6-month warranty
      Some models may also come with a free carry case or pre-installed software, depending on availability.
      `,
  },
  {
    question: "Do refurbished laptops come with software installed?",
    answer:
      "Yes, Edify laptops come with a genuine operating system pre-installed—usually Windows 10 or 11. Some models may also include essential software like Microsoft Office or antivirus tools, depending on the configuration.",
  },
  {
    question: "How does the warranty work for refurbished laptops?",
    answer:
      "All Edify laptops come with a standard 6-month warranty that covers key hardware components. You can also opt for extended warranty plans at checkout for added peace of mind.",
  },
  {
    question: "Are refurbished laptops environmentally friendly?",
    answer:
      "Absolutely. Buying refurbished means one less laptop ends up in a landfill, and significantly reduces e-waste. It’s a sustainable choice that lowers your carbon footprint while still giving you access to high-performance tech.",
  },
]

export interface FAQProps {
  faqs?: FAQItem[] // optional, will use defaultFaqs if not provided
  title?: string
  initialVisibleCount?: number
  className?: string
  viewMoreText?: string
  viewLessText?: string
}

export const FAQ: React.FC<FAQProps> = ({
  faqs = defaultFaqs,
  title = "Got Questions? We Have Answers",
  initialVisibleCount = 5,
  className = "flex w-full flex-col items-center gap-y-5 bg-white",
  viewMoreText = "View More",
  viewLessText = "View Less",
}) => {
  const [expanded, setExpanded] = useState<number | null>(null)
  const [showAll, setShowAll] = useState(false)

  const toggleFAQ = (index: number) => {
    setExpanded(expanded === index ? null : index)
  }

  const toggleShowAll = () => {
    setShowAll(!showAll)
  }

  const visibleFaqs = showAll ? faqs : faqs.slice(0, initialVisibleCount)

  return (
    <div className={`${className}`}>
      
      <div className="text-center text-lg  sm:text-xl lg:text-4xl font-orange leading-[44px] text-gray-950">
        {title}
      </div>

      {visibleFaqs?.map((faq, index) => (
        <div
          key={index}
          className={`flex flex-col w-full ${
            index !== 0 ? "border-t border-gray-300" : ""
          } pt-6`}
        >
          <div
            className="flex items-start justify-between cursor-pointer"
            onClick={() => toggleFAQ(index)}
          >
            <div className="text-sm lg:text-base xl:text-lg font-gilroySemiBold leading-7 text-gray-950">
              {faq?.question}
            </div>

            <FAQIcons.plus
              className={`transition-transform duration-300 transform:${
                expanded === index ? "rotate(45deg)" : "rotate(0)"
              }`}
            />
          </div>
          <div
            className={`overflow-hidden transition-all duration-300 ${
              expanded === index
                ? "max-h-[500px] opacity-100"
                : "max-h-0 opacity-0"
            }`}
          >
            <div className="font-gilroyMedium text-xs lg:text-sm leading-6 text-gray-500 pt-4">
              {faq.answer}
            </div>
          </div>
        </div>
      ))}

      {faqs?.length > initialVisibleCount && (
        <button
          className={buttonVariants({
            variant: "default",
            className:
              "w-fit font-gilroySemiBold border border-black px-9 lg:px-16 my-2",
          })}
          onClick={toggleShowAll}
        >
          {showAll ? viewLessText : viewMoreText}
        </button>
      )}
    </div>
  )
}
